import { useWorkPeriodsMeasureMultiple } from '../../../api/work-periods-client/work-periods-client.hooks';
import {
  Measure,
  TimeAllocationType,
  Transformer,
  type MeasureDataByEntity,
  type UseWorkPeriodsMeasurePayloadMultiple,
} from '../../../api/work-periods-client/work-periods-client.type';
import { formatDate } from '../../../helpers/date-time-helpers';
import { useProcessAnalysisStore } from '../../../store/process-analysis-store/process-analysis-store';
import {
  useAvailableMeasureNames,
  useDateRange,
  useEntities,
} from '../../../store/process-analysis-store/process-analysis-store.hooks';
import { getEntityType, splitMeasures } from '../../../helpers/process-analysis-shared/process-analysis-shared.helpers';
import { timeAllocationTransformerMeasures } from '../../../helpers/process-analysis-shared/process-analysis-shared.hooks';

/**
 * Custom hook that fetches monthly averages data based on process analysis store
 *
 * @param {boolean} [enabled=true] - Whether the hook should fetch data
 * @returns {Object} Object containing data and loading state
 */
export const useMonthlyAveragesData = (
  enabled = true,
): {
  data: MeasureDataByEntity | undefined;
  isFetching: boolean;
  isLoading: boolean;
} => {
  const { defaultMeasures, transformerMeasures } = useMeasureFilter();
  const customMeasures = useProcessAnalysisStore((state) => state.availableMeasures)
    .filter((measure) => measure.is_custom)
    .map((measure) => measure.measure_name);

  const groups = splitMeasures(defaultMeasures, 6);
  const customGroups = splitMeasures(customMeasures, 4);

  const {
    data: defaultDataFirst,
    isFetching: isFetchingDefaultDataFirst,
    isLoading: isLoadingDefaultDataFirst,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[0]),
    options: {
      enabled: enabled && !!groups[0]?.length,
    },
  });

  const {
    data: defaultDataSecond,
    isFetching: isFetchingDefaultDataSecond,
    isLoading: isLoadingDefaultDataSecond,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[1]),
    options: {
      enabled: enabled && !!groups[1]?.length,
    },
  });

  const {
    data: defaultDataThird,
    isFetching: isFetchingDefaultDataThird,
    isLoading: isLoadingDefaultDataThird,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[2]),
    options: {
      enabled: enabled && !!groups[2]?.length,
    },
  });

  const {
    data: defaultDataFourth,
    isFetching: isFetchingDefaultDataFourth,
    isLoading: isLoadingDefaultDataFourth,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[3]),
    options: {
      enabled: enabled && !!groups[3]?.length,
    },
  });

  const {
    data: defaultDataFifth,
    isFetching: isFetchingDefaultDataFifth,
    isLoading: isLoadingDefaultDataFifth,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[4]),
    options: {
      enabled: enabled && !!groups[4]?.length,
    },
  });

  const {
    data: defaultDataSixth,
    isFetching: isFetchingDefaultDataSixth,
    isLoading: isLoadingDefaultDataSixth,
  } = useWorkPeriodsMeasureMultiple({
    payload: usePayload(groups[5]),
    options: {
      enabled: enabled && !!groups[5]?.length,
    },
  });

  const {
    data: customDataFirst,
    isFetching: isFetchingCustomDataFirst,
    isLoading: isLoadingCustomDataFirst,
  } = useWorkPeriodsMeasureMultiple({
    payload: useCustomPayload(customGroups[0]),
    options: {
      enabled: enabled && !!customGroups[0]?.length,
    },
  });

  const {
    data: customDataSecond,
    isFetching: isFetchingCustomDataSecond,
    isLoading: isLoadingCustomDataSecond,
  } = useWorkPeriodsMeasureMultiple({
    payload: useCustomPayload(customGroups[1]),
    options: {
      enabled: enabled && !!customGroups[1]?.length,
    },
  });

  const {
    data: customDataThird,
    isFetching: isFetchingCustomDataThird,
    isLoading: isLoadingCustomDataThird,
  } = useWorkPeriodsMeasureMultiple({
    payload: useCustomPayload(customGroups[2]),
    options: {
      enabled: enabled && !!customGroups[2]?.length,
    },
  });

  const {
    data: customDataFourth,
    isFetching: isFetchingCustomDataFourth,
    isLoading: isLoadingCustomDataFourth,
  } = useWorkPeriodsMeasureMultiple({
    payload: useCustomPayload(customGroups[3]),
    options: {
      enabled: enabled && !!customGroups[3]?.length,
    },
  });

  const transformerPayload = useTransformerPayload(transformerMeasures);
  const {
    data: transformerData,
    isFetching: isFetchingTransformerData,
    isLoading: isLoadingTransformerData,
  } = useWorkPeriodsMeasureMultiple({
    payload: transformerPayload,
    options: {
      enabled: enabled && !!transformerMeasures.length,
    },
  });

  const data = Object.entries(defaultDataFirst || {})
    .concat(Object.entries(defaultDataSecond || {}))
    .concat(Object.entries(defaultDataThird || {}))
    .concat(Object.entries(defaultDataFourth || {}))
    .concat(Object.entries(defaultDataFifth || {}))
    .concat(Object.entries(defaultDataSixth || {}))
    .concat(Object.entries(transformerData || {}))
    .concat(Object.entries(customDataFirst || {}))
    .concat(Object.entries(customDataSecond || {}))
    .concat(Object.entries(customDataThird || {}))
    .concat(Object.entries(customDataFourth || {}))
    .reduce((acc, [key, value]) => {
      acc[key] = { ...acc[key], ...value };
      return acc;
    }, {} as MeasureDataByEntity);

  const isFetching =
    isFetchingDefaultDataFirst ||
    isFetchingDefaultDataSecond ||
    isFetchingDefaultDataThird ||
    isFetchingDefaultDataFourth ||
    isFetchingDefaultDataFifth ||
    isFetchingDefaultDataSixth ||
    isFetchingTransformerData ||
    isFetchingCustomDataFirst ||
    isFetchingCustomDataSecond ||
    isFetchingCustomDataThird ||
    isFetchingCustomDataFourth;
  const isLoading =
    isLoadingDefaultDataFirst ||
    isLoadingDefaultDataSecond ||
    isLoadingDefaultDataThird ||
    isLoadingDefaultDataFourth ||
    isLoadingDefaultDataFifth ||
    isLoadingDefaultDataSixth ||
    isLoadingTransformerData ||
    isLoadingCustomDataFirst ||
    isLoadingCustomDataSecond ||
    isLoadingCustomDataThird ||
    isLoadingCustomDataFourth;

  return { data, isFetching, isLoading };
};

/**
 * Custom hook that returns a payload for fetching work period measures.
 * Configured for monthly time allocation for monthly averages.
 *
 * @param {Measure[]} measures - Array of measure names to fetch
 * @returns {UseWorkPeriodsMeasurePayloadMultiple} The payload for fetching work period measures
 */
const usePayload = (measures: Measure[]): UseWorkPeriodsMeasurePayloadMultiple => {
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTaskType = useProcessAnalysisStore((state) => state.selectedTaskType);
  const { startDate, endDate } = useDateRange();
  const entities = useEntities();

  return {
    entity: getEntityType(activeTab),
    ids: entities,
    start_date: formatDate(startDate, { format: 'YYYY-MM-DD' }),
    end_date: formatDate(endDate, { format: 'YYYY-MM-DD' }),
    measure_name: measures,
    time_allocation_type: TimeAllocationType.Monthly,
    ...(selectedTaskType ? { task_type: selectedTaskType } : {}),
  };
};

/**
 * Custom hook that returns a payload for fetching transformer work period measures.
 * Similar to usePayload but includes the transformer parameter.
 *
 * @param {Measure[]} measures - Array of measure names to fetch
 * @returns {UseWorkPeriodsMeasurePayloadMultiple} The payload for fetching transformer work period measures
 */
const useTransformerPayload = (measures: Measure[]): UseWorkPeriodsMeasurePayloadMultiple => {
  const basePayload = usePayload(measures);
  return {
    ...basePayload,
    transformer: Transformer.TimeAllocationValuesOnly,
  };
};

/**
 * Custom hook that returns a payload for fetching custom work period measures.
 * Similar to usePayload but includes additional parameters for custom measures.
 *
 * @param {string[]} measures - Array of custom measure names to fetch
 * @returns {UseWorkPeriodsMeasurePayloadMultiple} The payload for fetching custom work period measures
 */
const useCustomPayload = (measures: string[]): UseWorkPeriodsMeasurePayloadMultiple => {
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTaskType = useProcessAnalysisStore((state) => state.selectedTaskType);
  const { startDate, endDate } = useDateRange();
  const entities = useEntities();

  return {
    entity: getEntityType(activeTab),
    ids: entities,
    start_date: formatDate(startDate, { format: 'YYYY-MM-DD' }),
    end_date: formatDate(endDate, { format: 'YYYY-MM-DD' }),
    measure_name: measures,
    time_allocation_type: TimeAllocationType.Monthly,
    transformer: Transformer.ReturnFirstMetricOnly,
    is_custom: true,
    ...(selectedTaskType ? { task_type: selectedTaskType } : {}),
  };
};

/**
 * Custom hook that filters measures into two categories:
 *   - `defaultMeasures`: measures that don't have a transformer
 *   - `transformerMeasures`: measures that have a transformer
 *
 * @returns {{ defaultMeasures: Measure[]; transformerMeasures: Measure[] }} - An object containing both types of measures.
 */
const useMeasureFilter = (): { defaultMeasures: Measure[]; transformerMeasures: Measure[] } => {
  const measures = useAvailableMeasureNames();

  return measures.reduce(
    (acc, measure) => {
      const isTransformerMeasure = timeAllocationTransformerMeasures.includes(measure);

      if (isTransformerMeasure) {
        return { ...acc, transformerMeasures: [...acc.transformerMeasures, measure] };
      }

      return { ...acc, defaultMeasures: [...acc.defaultMeasures, measure] };
    },
    { defaultMeasures: [] as Measure[], transformerMeasures: [] as Measure[] },
  );
};
